import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { KpiTile, SectionHead } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useMe } from '@/features/auth/api'
import { errorMessage, isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, numberFmt } from '@/lib/format'
import {
  type GroupFilters,
  type MarketingGroup,
  type MarketingList,
  useAudience,
  useDeleteGroup,
  useMarketing,
  useSaveGroup,
} from './api'
import { FilterFields } from './filters'
import { chipFor, describeFilters, dollars, NOT_TRACKED, openedClicked, percent } from './format'
import { MarketingSends } from './sends'

/**
 * Marketing emails (Engage → Marketing): email-only campaigns to a group of the people who opted
 * in to product news, with their results, and the saved groups.
 */
export function MarketingPage() {
  const list = useMarketing()
  const me = useMe()
  const navigate = useNavigate()
  const canWrite = me.data?.role === 'owner' || me.data?.role === 'admin'

  return (
    <>
      <PageTitle
        title="Marketing"
        description="Email customers: marketing to the people who opted in, or a service notice to everyone it concerns. Choose who, write it, send yourself a test, then send or schedule it."
        actions={
          canWrite && (
            <Link to="/marketing/new" className="btn primary">
              <Plus aria-hidden="true" />
              New marketing email
            </Link>
          )
        }
      />
      {list.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : list.isError ? (
        <div className="card">
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        </div>
      ) : (
        <>
          <Kpis list={list.data} />
          {list.data.data.length === 0 ? (
            <div className="card">
              <EmptyState
                title="No emails yet"
                description="Choose marketing or a service notice and a group, write the email and send yourself a test. Nothing goes out until an owner sends it."
              />
            </div>
          ) : (
            <div className="tw">
              <table className="t" style={{ minWidth: 820 }}>
                <caption className="sr-only">Marketing emails</caption>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th className="r">Sent</th>
                    <th className="r">Opened / clicked</th>
                    <th className="r">Purchases after</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.map((c) => (
                    <tr key={c.id} className="click" onClick={() => void navigate(`/marketing/${c.id}`)}>
                      <td>
                        <Link
                          to={`/marketing/${c.id}`}
                          style={{ fontWeight: 600, color: 'var(--ink)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.name}
                        </Link>
                        <small>
                          {list.data.meta.kinds[c.kind].label} · {c.subject}
                        </small>
                      </td>
                      <td>
                        <span className={cn('chip', chipFor(c).tone)}>{chipFor(c).label}</span>
                      </td>
                      <td className="r num">{c.results ? numberFmt.format(c.results.progress.sent) : '—'}</td>
                      <td className="r num">
                        {c.results && !c.results.counts_opens ? (
                          <span className="m" title={NOT_TRACKED}>
                            Not tracked
                          </span>
                        ) : (
                          ((c.results && openedClicked(c.results)) ?? '—')
                        )}
                      </td>
                      <td className="r num">
                        {c.results
                          ? `${numberFmt.format(c.results.purchases.workspaces)} · ${dollars(c.results.purchases.revenue_usd)}`
                          : '—'}
                      </td>
                      <td className="m" style={{ whiteSpace: 'nowrap' }}>
                        {c.sent_at
                          ? formatDate(c.sent_at)
                          : c.scheduled_at
                            ? formatDateTime(c.scheduled_at)
                            : c.updated_at
                              ? formatDate(c.updated_at)
                              : '—'}
                        {(c.sent_by ?? c.scheduled_by ?? c.updated_by) && (
                          <small>{c.sent_by ?? c.scheduled_by ?? c.updated_by}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Groups list={list.data} canWrite={canWrite} />
          <MarketingSends campaigns={list.data.data} />
        </>
      )}
    </>
  )
}

function Kpis({ list }: { list: MarketingList }) {
  const sent = list.data.filter((c) => c.results !== null)
  const total = (pick: (r: NonNullable<(typeof sent)[number]['results']>) => number) =>
    sent.reduce((sum, c) => sum + (c.results ? pick(c.results) : 0), 0)
  // Opens and clicks of marketing emails only: service notices aren't tracked.
  const trackedResults = sent.flatMap((c) => (c.results?.counts_opens ? [c.results] : []))
  const sum = (pick: (r: (typeof trackedResults)[number]) => number | null) =>
    trackedResults.reduce((n, r) => n + (pick(r) ?? 0), 0)
  const tracked = sum((r) => r.tracked)
  const onlyService = sent.length > 0 && trackedResults.length === 0

  return (
    <div className="kpis">
      <KpiTile
        label="Owners who opted in"
        value={numberFmt.format(list.meta.opted_in)}
        compare={`${numberFmt.format(list.meta.everyone_opted_in)} with their teams`}
        hint="Workspace owners with a confirmed address who ticked product news, leaving out internal accounts and addresses that bounced or complained."
      />
      <KpiTile
        label="Customers"
        value={numberFmt.format(list.meta.customers)}
        compare="workspace owners a service notice reaches"
        hint="Every owner with a confirmed address and an open account, opted in or not. Service notices are only for essential changes to the service."
      />
      <KpiTile
        label="Emails sent"
        value={numberFmt.format(total((r) => r.progress.sent))}
        compare={`${numberFmt.format(sent.length)} ${sent.length === 1 ? 'campaign' : 'campaigns'}`}
      />
      <KpiTile
        label="Opened / clicked"
        value={
          tracked > 0
            ? `${percent(
                sum((r) => r.opened),
                tracked,
              )} / ${percent(
                sum((r) => r.clicked),
                tracked,
              )}`
            : onlyService
              ? 'Not tracked'
              : '—'
        }
        compare={
          onlyService ? 'only service notices sent so far' : 'of the marketing emails Amazon SES tracked'
        }
        hint={`Marketing emails only. ${NOT_TRACKED} Opens are approximate: some mail apps open every email, others block the tracking image.`}
      />
      <KpiTile
        label="Purchases after"
        value={dollars(total((r) => r.purchases.revenue_usd))}
        compare={`by ${numberFmt.format(total((r) => r.purchases.workspaces))} workspaces within ${list.meta.conversion_days} days`}
        hint="Paid invoices of the workspaces whose owner got the email, in the days after it went out. They may have bought anyway."
      />
    </div>
  )
}

/** Saved groups: named filters to start an email from, counted again whenever they're used. */
function Groups({ list, canWrite }: { list: MarketingList; canWrite: boolean }) {
  const [editing, setEditing] = useState<MarketingGroup | 'new' | null>(null)
  const [deleting, setDeleting] = useState<MarketingGroup | null>(null)
  const remove = useDeleteGroup()

  return (
    <section className="sec">
      <SectionHead
        title="Saved groups"
        extra={
          canWrite && (
            <button type="button" className="btn sm" onClick={() => setEditing('new')}>
              <Plus aria-hidden="true" /> New group
            </button>
          )
        }
      />
      {list.groups.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No saved groups"
            description="Save a group you email often, such as Free owners who never connected WordPress, and start each email from it."
          />
        </div>
      ) : (
        <div className="tw">
          <table className="t" style={{ minWidth: 640 }}>
            <caption className="sr-only">Saved groups</caption>
            <thead>
              <tr>
                <th>Group</th>
                <th className="r">People now</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.groups.map((g) => (
                <tr key={g.id}>
                  <td>
                    <b>{g.name}</b>
                    <small style={{ whiteSpace: 'normal' }}>{describeFilters(g.filters, list.meta)}</small>
                  </td>
                  <td className="r num">{numberFmt.format(g.count)}</td>
                  <td className="r" style={{ whiteSpace: 'nowrap' }}>
                    {canWrite && (
                      <>
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label={`Edit ${g.name}`}
                          onClick={() => setEditing(g)}
                        >
                          <Pencil aria-hidden="true" /> Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label={`Delete ${g.name}`}
                          onClick={() => setDeleting(g)}
                        >
                          <Trash2 aria-hidden="true" /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing !== null && (
        <GroupDialog
          key={editing === 'new' ? 'new' : editing.id}
          group={editing === 'new' ? null : editing}
          meta={list.meta}
          onClose={() => setEditing(null)}
        />
      )}
      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent
          title={`Delete “${deleting?.name ?? ''}”?`}
          description="Emails that started from it keep their own copy of its filters."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() =>
                  deleting &&
                  remove.mutate(deleting.id, {
                    onSuccess: () => {
                      toast.success('Group deleted.')
                      setDeleting(null)
                    },
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              >
                Delete
              </Button>
            </>
          }
        />
      </Dialog>
    </section>
  )
}

/** Name a group and choose its filters, with how many people it reaches now. */
export function GroupDialog({
  group,
  meta,
  initialFilters,
  onClose,
}: {
  group: MarketingGroup | null
  meta: MarketingList['meta']
  initialFilters?: GroupFilters
  onClose: () => void
}) {
  const save = useSaveGroup(group?.id)
  const [name, setName] = useState(group?.name ?? '')
  const [filters, setFilters] = useState<GroupFilters>(group?.filters ?? initialFilters ?? {})
  const [error, setError] = useState<string | undefined>()
  const audience = useAudience(filters, 0)

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={group ? `Edit “${group.name}”` : 'New group'}
        description={
          audience.data
            ? `Reaches ${numberFmt.format(audience.data.count)} ${audience.data.count === 1 ? 'person' : 'people'} now. It’s counted again whenever an email uses it.`
            : 'Counting the people it reaches…'
        }
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              loading={save.isPending}
              disabled={name.trim() === ''}
              onClick={() =>
                save.mutate(
                  { name: name.trim(), filters },
                  {
                    onSuccess: () => {
                      toast.success(group ? 'Group saved.' : 'Group created.')
                      onClose()
                    },
                    onError: (err) =>
                      isApiError(err) && err.isValidation
                        ? setError(Object.values(err.errors)[0]?.[0])
                        : toast.error(errorMessage(err)),
                  },
                )
              }
            >
              {group ? 'Save group' : 'Create group'}
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Field label="Name" htmlFor="group-name" required error={error}>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Free owners, no WordPress yet"
            />
          </Field>
          <FilterFields filters={filters} meta={meta} onChange={setFilters} idPrefix="group" />
        </div>
      </DialogContent>
    </Dialog>
  )
}
